import type { AxiosError } from 'axios';
import type { Queue, Tag, Template } from 'lib-shared-legacy-types';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import type { BatchSchedule } from 'lib-shared-types-api';
import memoize from 'memoize-one';
import { PureComponent } from 'react';

import { BusinessHoursEmailNodeAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/businesshours';
import { CloseNodeEmailAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/close';
import { FiltersNodeEmailAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/filters';
import { InitNodeEmailAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/init';
import { IntegrationNodeEmailAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/integration';
import { OpeningHoursDeprecatedNodeAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/openingHours';
import { QueueNodeEmailAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/queue';
import { TagsNodeAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/tags';
import { ScheduleFetch } from 'components/powerFlowShared/components/scheduleFetch';
import { TagFetch } from 'components/powerFlowShared/components/tagFetch';
import { TemplatesFetch } from 'components/powerFlowShared/components/templatesFetch';
import { actionDiff } from 'components/powerFlowShared/helpers/actionDiff';
import { FlowEditorChrome, FlowType } from 'fable/flowEditor';
import { RouteComponentProps } from 'hocs/router';

import {
  FiltersNodeEmail,
  IntegrationNodeEmail,
  OpeningHoursNode,
  TagsNode,
} from '../../powerFlowShared/components/nodeTypes/';
import { FlowNodeTypes } from '../../powerFlowShared/components/types';
import { getActionNodes } from '../../powerFlowShared/helpers/getInvalidNodes';
import {
  FlowEmailActionT,
  FlowEmailUpdateFlowT,
  SaveStatus,
} from '../api/types';
import {
  FlowEmailDispatchPropsT,
  FlowEmailStatePropsT,
} from '../containers/powerEmailFlow';

import {
  CloseNode,
  ContentNode,
  InitNode,
  QueueNode,
  ReplyNode,
} from './nodeTypes';
import { ReplyNodeAttributes } from './nodeTypes/canvas/reply';

import '../styles/email-flow.less';

export type PowerEmailFlowExtraDataT = {
  name: string;
  tags: Tag[];
  schedules: BatchSchedule[];
  queues: Queue[];
  templates: Template[];
};

export type FlowEmailOwnPropsT = RouteComponentProps<'flowId'>;

type FlowEmailPropsT = FlowEmailOwnPropsT &
  FlowEmailStatePropsT &
  FlowEmailDispatchPropsT;

type FlowEmailStateT = {
  actions: FlowEmailActionT[] | null;
  active?: boolean | null;
  invalidNodes: string[] | null;
  name: string;
  nodeTypes: {};
  number: string | null;
  ready: boolean;
  saveStatus: SaveStatus;
};

class PowerEmailFlow extends PureComponent<FlowEmailPropsT, FlowEmailStateT> {
  static getDerivedStateFromProps(
    props: FlowEmailPropsT,
    state: FlowEmailStateT,
  ) {
    if (
      props.status === 'succeeded' &&
      !state.ready &&
      props.flowData.name &&
      props.flowData.actions.length
    ) {
      // flow is ready, and hasn't been initialized yet..
      // we update flow info in state
      return {
        saveStatus: SaveStatus.SAVED,
        ready: true,
        name: props.flowData.name,
        actions: props.flowData.actions,
        active: true,
      };
    }
    if (props.status === 'failed') {
      return { saveStatus: SaveStatus.FAILED };
    }
    if (
      props.status === 'succeeded' &&
      (state.saveStatus === SaveStatus.SAVING ||
        state.saveStatus === SaveStatus.FAILED)
    ) {
      return { saveStatus: SaveStatus.SAVED };
    }

    // no change
    return null;
  }

  constructor(props: FlowEmailPropsT) {
    super(props);

    const { isCanvasEnabled, isIntegrationsEnabled } = props;

    this.state = {
      actions: null,
      active: true,
      invalidNodes: null,
      name: '',
      nodeTypes: isCanvasEnabled
        ? {
            [FlowNodeTypes.INIT]: InitNodeEmailAttributes,
            // GREEN - Termination -> Queue
            [FlowNodeTypes.QUEUE]: QueueNodeEmailAttributes,
            // RED - Termination -> Closed
            [FlowNodeTypes.CLOSE]: CloseNodeEmailAttributes,
            // PURPLE - Splitters
            [FlowNodeTypes.FILTERS]: FiltersNodeEmailAttributes,
            [FlowNodeTypes.OPENING_HOURS]: OpeningHoursDeprecatedNodeAttributes,
            [FlowNodeTypes.BUSINESS_HOURS]: BusinessHoursEmailNodeAttributes,
            // BLUE - Other
            ...(isIntegrationsEnabled
              ? { [FlowNodeTypes.INTEGRATION]: IntegrationNodeEmailAttributes }
              : {}),
            [FlowNodeTypes.TAGS]: TagsNodeAttributes,
            [FlowNodeTypes.AUTOREPLY]: ReplyNodeAttributes,
          }
        : {
            [FlowNodeTypes.INIT]: InitNode,
            // GREEN - Termination -> Queue
            [FlowNodeTypes.QUEUE]: QueueNode,
            // RED - Termination -> Closed
            [FlowNodeTypes.CLOSE]: CloseNode,
            // PURPLE - Splitters
            [FlowNodeTypes.FILTERS]: FiltersNodeEmail,
            [FlowNodeTypes.CONTENT]: ContentNode,
            [FlowNodeTypes.OPENING_HOURS]: OpeningHoursNode,
            // ORANGE - Input required
            // BLUE - Other
            ...(isIntegrationsEnabled
              ? { [FlowNodeTypes.INTEGRATION]: IntegrationNodeEmail }
              : {}),
            [FlowNodeTypes.TAGS]: TagsNode,
            [FlowNodeTypes.AUTOREPLY]: ReplyNode,
          },
      number: null,
      ready: false,
      saveStatus: SaveStatus.SAVED,
    };
  }

  componentDidMount() {
    this.props.getAllQueues();

    this.props
      .fetchFlow({
        id: this.props.id,
        email_address: this.props.email_address,
      })
      .catch((err: Error) => {
        console.error('fetchFlow exception', err);
        this.props.notificationAdd({
          text: 'Can not fetch email flow without an ID',
          icon: 'i-info',
          className: 'warning',
          title: 'Error on fetching flow',
        });
      });
  }

  componentWillUnmount() {
    this.props.resetState();
  }

  hasUnsavedChanges = () => {
    return this.state.saveStatus <= 0;
  };

  handleNumberChange = (number: { e164_format: string }) => {
    if (this.state.number !== number.e164_format) {
      this.setState({
        number: number.e164_format,
        saveStatus: SaveStatus.UNSAVED,
      });
    }
  };

  handleOnChange = ({
    name,
    actions,
    active,
  }: {
    name: string;
    actions: FlowEmailActionT[];
    active: boolean;
  }) => {
    if (
      !this.hasUnsavedChanges() ||
      this.state.name !== name ||
      this.state.actions !== actions ||
      this.state.active !== active
    ) {
      this.setState({
        name,
        actions,
        active,
        saveStatus: SaveStatus.UNSAVED,
        invalidNodes: null,
      });
    }
  };

  getFullFlow = (local = false) => {
    const { name, actions } = this.state;
    const { id, email_address, agentDisplayName } = this.props;

    return this.getFullFlowMemo(
      local,
      id,
      name,
      actions,
      email_address,
      agentDisplayName,
    );
  };

  getFullFlowMemo = memoize(
    (_local, id = 'local', name, actions, location, agentDisplayName) => {
      return {
        id,
        name: name || 'Name your flow',
        actions,
        active: true,
        location,
        updated_by: agentDisplayName || null,
      };
    },
  );

  handleOnSave = () => {
    const { actions } = this.state;
    let invalidNodes;

    if (actions) {
      invalidNodes = getActionNodes(actions);
    }

    if (invalidNodes && invalidNodes.length) {
      this.setState({
        invalidNodes,
      });
    } else {
      this.setState(
        {
          saveStatus: SaveStatus.SAVING,
          invalidNodes: null,
        },
        this.saveOrUpdateFlow,
      );
    }
  };

  saveOrUpdateFlow = () => {
    const fullFlow = this.getFullFlow();

    this.updateFlow(fullFlow);
  };

  updateFlow = (fullFlow: FlowEmailUpdateFlowT) => {
    const { id, notificationAdd, updateFlow, flowData } = this.props;

    updateFlow({ id, flow: fullFlow })
      .then(() => {
        const notificationOptions = {
          title: 'Saved',
          text: `"${this.state.name}" is saved.`,
          icon: 'i-done',
          className: 'success',
          time: 3000,
          showDesktop: false,
        };
        notificationAdd(notificationOptions);
      })
      .catch((err: AxiosError<any>) => {
        console.error(err);
        Sentry.captureException(err, {
          contexts: {
            response: err?.response?.data?.data,
            flow: {
              data: fullFlow,
              diff: actionDiff(fullFlow.actions, flowData.actions),
            },
          },
          tags: { team: SentryTeam.Routing },
        });
        const notificationOptions = {
          title: 'Could not save',
          text: `There was an issue when "${
            this.state.name
          }" was saved. ${err.toString()}`,
          icon: 'i-exclamation',
          className: 'warning',
          showDesktop: false,
        };
        notificationAdd(notificationOptions);
        this.setState({
          saveStatus: SaveStatus.FAILED,
        });
      });
  };

  handleOnActivate = () => {
    this.setState(
      {
        active: true,
      },
      this.saveOrUpdateFlow,
    );
  };

  topLeftButtonProps = {
    changeNumber: this.handleNumberChange,
  };

  getExtraData = memoize(
    (
      name: string,
      tags: Tag[],
      schedules: BatchSchedule[],
      queuesState: Queue[],
      templates: Template[],
    ): PowerEmailFlowExtraDataT => ({
      name,
      tags,
      schedules,
      queues: queuesState || [],
      templates,
    }),
  );

  render() {
    const { flowData, queuesState } = this.props;
    const { nodeTypes, ready, invalidNodes, actions, name } = this.state;

    return (
      <TemplatesFetch
        actions={actions}
        render={(templates) => (
          <TagFetch
            actions={actions}
            render={(tags) => (
              <ScheduleFetch
                actions={actions}
                render={(schedules) => (
                  <div className="powercallflow-container">
                    {ready && (
                      <FlowEditorChrome
                        flowType={FlowType.FLOW_TYPE_EMAIL}
                        errorMessage={null}
                        extraData={this.getExtraData(
                          name,
                          tags.items,
                          schedules,
                          queuesState,
                          templates.items,
                        )}
                        goBackPath="/settings/email"
                        hasUnsavedChanges={this.hasUnsavedChanges()}
                        initialData={flowData}
                        invalidNodes={invalidNodes}
                        nodeTypes={nodeTypes}
                        onActivate={this.handleOnActivate}
                        onChange={this.handleOnChange}
                        onSave={this.handleOnSave}
                      />
                    )}
                  </div>
                )}
              />
            )}
          />
        )}
      />
    );
  }
}

export default PowerEmailFlow;
